import { C } from '@/theme/palette';
/**
 * backlogModel.js — derivações compartilhadas do Product Backlog (Fase 5).
 *
 * Fonte de verdade: GET /projects/{id}/backlog → { epics[], cycles[] }, com
 * epics: [{ id, title, description, cycleNumber, features:[{ id, title,
 * stories:[{ id, title, type:'story'|'task', points, acceptance[], reuse, deps[] }] }] }].
 *
 * Usado por BacklogScreen, CyclesScreen e GateEscopoScreen para manter KPIs,
 * pontos efetivos, tokens e custo idênticos (igual ao protótipo).
 */

// Tipos de item do backlog (label + cor) — espelha BACKLOG_TYPE do protótipo.
export const BACKLOG_TYPE = {
  epic:    { label: 'ÉPICO',        color: '#5C00E0' },
  feature: { label: 'FEATURE',      color: '#7400FE' },
  story:   { label: 'USER STORY',   color: C.cyber },
  task:    { label: 'ATIV. TÉCNICA', color: C.digital },
};

// Custo por token (US$) e tokens estimados por story point — estimativa honesta
// derivada dos pontos (o sistema não mede tokens de antemão). US$ 10 por milhão: Opus 4.8
// a US$ 5 de entrada e US$ 25 de saída, na proporção de 3 de entrada para 1 de saída.
export const TOKEN_RATE = 0.00001;
export const TOKENS_PER_POINT = 14750;

/** Pontos efetivos: itens cobertos por reuso custam ~40% do esforço. */
export function effectivePoints(leaf) {
  const p = Number(leaf?.points || 0);
  return leaf?.reuse ? Math.max(1, Math.round(p * 0.4)) : p;
}

export const leafTokens = (leaf) => effectivePoints(leaf) * TOKENS_PER_POINT;
export const leafCost = (leaf) => leafTokens(leaf) * TOKEN_RATE;
export const usd = (n) => `US$ ${Number(n || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** Achata a árvore em folhas (story|task), anexando o ciclo herdado do épico. */
export function flattenLeaves(epics) {
  const out = [];
  (epics || []).forEach((ep) => {
    (ep.features || []).forEach((ft) => {
      (ft.stories || []).forEach((s) => {
        out.push({ ...s, cycle: s.cycle ?? ep.cycleNumber ?? null, epicId: ep.id, epicTitle: ep.title });
      });
    });
  });
  return out;
}

/** KPIs consolidados do backlog. */
export function backlogStats(epics) {
  const leaves = flattenLeaves(epics);
  const features = (epics || []).reduce((a, ep) => a + (ep.features?.length || 0), 0);
  const stories = leaves.filter((l) => (l.type || 'story') === 'story').length;
  const tasks = leaves.filter((l) => l.type === 'task').length;
  const withDeps = leaves.filter((l) => Array.isArray(l.deps) && l.deps.length > 0);
  const reused = leaves.filter((l) => l.reuse).length;
  const totalPts = leaves.reduce((a, l) => a + effectivePoints(l), 0);
  const totalTokens = leaves.reduce((a, l) => a + leafTokens(l), 0);
  return {
    epics: (epics || []).length, features, stories, tasks,
    items: (epics || []).length + features + leaves.length,
    leaves, withDeps, reused, totalPts, totalTokens,
    totalCost: totalTokens * TOKEN_RATE,
  };
}

/** Itens (folhas) por número de ciclo. */
export function leavesByCycle(epics) {
  const map = new Map();
  flattenLeaves(epics).forEach((l) => {
    const k = l.cycle ?? 0;
    if (!map.has(k)) map.set(k, []);
    map.get(k).push(l);
  });
  return map;
}
